import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from "@mui/material";
import api from "../api/axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

/* ================= TEMPLATE CONFIG ================= */
const templateConfig = [
  {
    name: "sppl_brochure",
    label: "Send Our Brochure",
    fields: [],
  },
  {
    name: "customer_feedback",
    label: "Request Google Review",
    fields: [],
  },
  {
    name: "client_welcome",
    label: "Welcome Customer",
    fields: [],
  },
  {
    name: "appointment",
    label: "Request for Appointment",
    fields: [{ name: "purpose", label: "Purpose / Regarding" }],
  },
  {
    name: "clarification_en",
    label: "Project Clarification Needed",
    fields: [{ name: "project", label: "Project Name" }],
  },
];

export default function WhatsappTemplates() {
  const [formState, setFormState] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(null);

  /* ================= HANDLE INPUT ================= */
  const handleChange = (template, field, value) => {
    setFormState((prev) => ({
      ...prev,
      [template]: {
        ...prev[template],
        [field]: value,
      },
    }));
  };

  /* ================= SEND MESSAGE ================= */
  const handleSubmit = async (template) => {
    const data = formState[template] || {};

    if (!/^[0-9]{10}$/.test(data.mobile || "")) {
      alert("Enter valid 10 digit mobile number");
      return;
    }

    try {
      setLoadingTemplate(template);

      await api.post("/whatsapp/send-template", {
        template,
        mobile: data.mobile,
        params: data,
      });

      alert("Message sent ✅");
      setFormState({});
    } catch (err) {
      console.error(err);
      alert("Failed ❌");
    } finally {
      setLoadingTemplate(null);
    }
  };

  /* ================= VIEW HISTORY ================= */
  const handleViewHistory = async (template) => {
    try {
      const { data } = await api.get(`/whatsapp/history/${template}`);
      setHistoryData(data.data || []);
      setHistoryOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Client WhatsApp Messaging
      </Typography>

      <Stack spacing={3}>
        {templateConfig.map((tpl) => {
          const form = formState[tpl.name] || {};

          return (
            <Box
              key={tpl.name}
              sx={{
                p: 3,
                borderRadius: 3,
                backdropFilter: "blur(10px)",
                background: "rgba(255,255,255,0.06)",
              }}
            >
              <Typography fontWeight={600} mb={2}>
                {tpl.label}
              </Typography>

              <Grid container spacing={2}>
                {/* Mobile */}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="10 Digit Mobile No."
                    fullWidth
                    value={form.mobile || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d{0,10}$/.test(val)) {
                        handleChange(tpl.name, "mobile", val);
                      }
                    }}
                  />
                </Grid>

                {/* Dynamic Fields */}
                {tpl.fields.map((field) => (
                  <Grid item xs={12} md={3} key={field.name}>
                    <TextField
                      type={field.type || "text"}
                      label={field.label}
                      fullWidth
                      InputLabelProps={
                        field.type === "date" ? { shrink: true } : undefined
                      }
                      value={form[field.name] || ""}
                      onChange={(e) =>
                        handleChange(tpl.name, field.name, e.target.value)
                      }
                    />
                  </Grid>
                ))}

                {/* Buttons */}
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleSubmit(tpl.name)}
                      disabled={loadingTemplate === tpl.name}
                    >
                      Send
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleViewHistory(tpl.name)}
                    >
                      View Log
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {/* ================= HISTORY MODAL ================= */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Message Log</DialogTitle>

        <DialogContent>
          {historyData.length === 0 ? (
            <Typography>No history found</Typography>
          ) : (
            <Stack spacing={1}>
              {historyData.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <Typography variant="body2">📞 {item.mobile_no}</Typography>
                  <Typography variant="caption">
                    📅 {dayjs(item.created_at).format("DD-MM-YYYY hh:mm:ss a")}
                  </Typography>
                  <Typography variant="body2">💬 {item.params}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
